import Banner from '@cpg-its/ads.containers.banner';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { Hidden } from '@material-ui/core';
import React from 'react';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { TcsmStates } from '../../../../root/tcsm-model';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { MainContent } from '../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../global/components/layout/page-container';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { MyCPGMidYearProps } from './models/mycpg-midyear-epi-props';
import { DividerSection } from '../../../../../stories/divider-section';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { WysiwygWithReactLink } from '../../../../global/components/wysiwyg-with-react-link';
import { getTimeNewHireConfirmationTime } from '../../../../global/helpers/date-helpers';
import { useGpsType } from '../../../../global/hooks/useGpsType';
import { ReviewCoverageDisplay } from '../../components/read-only/review-coverage-display';
import { ReviewPlanDisplay } from '../../components/read-only/review-plan-display';
import { MidYearConfirmationModel, MyCPGMidYearConfirmationModel } from './models/mycpg-mid-year-models';
import { ReviewPlanExtendedDisplay } from '../../components/read-only/review-plan-extended-display';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { AccountInfoRoutes } from '../../constants/routes';
import { ButtonWrapper } from '../../../../global/components/layout/button-wrapper';
import { buttonStyles } from '../../../../global/constants/theme/styles/components/buttonstyles';
import { ExternalLink } from '../../../../global/components/external-link-modal';

type Status = 'success' | 'error' | 'info' | 'warning';
interface Props {
    model: MyCPGMidYearConfirmationModel;
}

export function MyCPGMidYearConfirmationView(props: Props) {

    const { model } = props;

    const location = useLocation();
    const history = useHistory();

    const myCPGMidyearPropsLoaded = useSelector<TcsmStates, boolean>(state => state.epiProps?.myCpgMidyearSettingsState?.contentLoaded);
    const myCPGMidyearProps = useSelector<TcsmStates, MyCPGMidYearProps>(state => state?.epiProps?.myCpgMidyearSettingsState?.epiContent);
    const gpsItem = useGpsType(model?.gpsType);
    const currentNYTime = getTimeNewHireConfirmationTime();

    const senderState: any = location?.state;
    const confirmationModel: MidYearConfirmationModel = senderState?.midYearConfirmationModel;
    const buttonClasses = buttonStyles();

    const myCpgHome = useBasePath(AccountInfoRoutes.MyCpgRoot);

    const { coveredPeopleMedical, coveredPeopleDental, hasMedicalEnrolled, hasDentalEnrolled,
        selectedMedicalPlan, selectedDentalPlan, selectedGroupLifePlan, selectedLongTermDisability, selectedShortTermDisability, selectedUei,
        hasDentalAvailable, hasGroupLife, hasLongTermDisability, hasMedicalAvailable, hasShortTermDisability, hasUei } = confirmationModel;

    function RedirectToPage(url: string) {
        history.push(url);
    }

    function RenderDocument(documentName: string, documentUrl: string) {
        if (documentUrl) {
            return (
                <p><ExternalLink linkIcon={<PictureAsPdfIcon />} newWindow linkText={documentName || documentUrl} linkUrl={documentUrl} /></p>
            );
        }
        return null;
    }

    function RenderDocuments() {
        const documents = [];
        if (selectedMedicalPlan?.planPdf) {
            documents.push(selectedMedicalPlan?.planPdf);
        }

        if (selectedDentalPlan?.planPdf) {
            documents.push(selectedDentalPlan?.planPdf);
        }

        if (selectedGroupLifePlan?.planPdf) {
            documents.push(selectedGroupLifePlan?.planPdf);
        }

        if (selectedShortTermDisability?.planPdf) {
            documents.push(selectedShortTermDisability?.planPdf);
        }

        if (selectedLongTermDisability?.planPdf) {
            documents.push(selectedLongTermDisability?.planPdf);
        }

        if (selectedUei?.planPdf) {
            documents.push(selectedUei?.planPdf);
        }

        if (documents.length === 0) return null;

        return (
            <ContentSpacer>
                <p>{myCPGMidyearProps?.documentDownloadInstructionText}</p>
                {RenderDocument(myCPGMidyearProps?.medicalDocumentLabel, selectedMedicalPlan?.planPdf)}
                {RenderDocument(myCPGMidyearProps?.dentalDocumentLabel, selectedDentalPlan?.planPdf)}
                {RenderDocument(myCPGMidyearProps?.groupLifeDocumentLabel, selectedGroupLifePlan?.planPdf)}
                {RenderDocument(myCPGMidyearProps?.shortTermDisabilityDocumentLabel, selectedShortTermDisability?.planPdf)}
                {RenderDocument(myCPGMidyearProps?.longTermDisabilityDocumentLabel, selectedLongTermDisability?.planPdf)}
                {RenderDocument(myCPGMidyearProps?.ueiDocumentLabel, selectedUei?.planPdf)}
            </ContentSpacer>
        );
    }

    if (!myCPGMidyearPropsLoaded) {
        return <LoadingCircle />;
    }

    return (
        <>
            <PageContainer>
                <MainContent>
                    <ContentSpacer>
                        <Banner
                            banner={{
                                type: 'warning',
                                label: <WysiwygWithReactLink content={myCPGMidyearProps?.confirmationAlertDescriptionText} />,

                            }}
                        />
                    </ContentSpacer>
                    <ContentSpacer>
                        <Banner
                            banner={{
                                type: model?.alertType as Status,
                                label: <WysiwygWithReactLink content={model?.alertDescription} />,

                            }}
                        />
                    </ContentSpacer>
                    <PageHeader
                        headerText={model?.title}
                        description={model?.description?.replace('{date}', currentNYTime)}
                    />
                    <DividerSection sectionText={myCPGMidyearProps?.confirmationPlanDetailTitle}>
                        {RenderDocuments()}
                        <ReviewCoverageDisplay
                            title={myCPGMidyearProps?.confirmationMedicalCoverageSectionTitle}
                            declinedMessage={myCPGMidyearProps?.medicalCoverageDeclinedText}
                            coveredPeople={coveredPeopleMedical}
                            enrolled={hasMedicalEnrolled}
                            planAvailable={hasMedicalAvailable}
                        />

                        <ReviewCoverageDisplay
                            title={myCPGMidyearProps?.confirmationDentalCoverageSectionTitle}
                            declinedMessage={myCPGMidyearProps?.dentalCoverageDeclinedText}
                            coveredPeople={coveredPeopleDental}
                            enrolled={hasDentalEnrolled}
                            planAvailable={hasDentalAvailable}
                        />

                        <ReviewPlanDisplay
                            title={myCPGMidyearProps?.confirmationMedicalPlanSectionTitle}
                            declinedMessage={myCPGMidyearProps?.medicalPlanDeclinedText}
                            plan={selectedMedicalPlan}
                            enrolled={hasMedicalEnrolled}
                            planAvailable={hasMedicalAvailable}
                        />

                        <ReviewPlanDisplay
                            title={myCPGMidyearProps?.confirmationDentalPlanSectionTitle}
                            declinedMessage={myCPGMidyearProps?.dentalPlanDeclinedText}
                            plan={selectedDentalPlan}
                            enrolled={hasDentalEnrolled}
                            planAvailable={hasDentalAvailable}
                        />

                        <ReviewPlanExtendedDisplay
                            title={myCPGMidyearProps?.confirmationGroupLifeSectionTitle}
                            plan={selectedGroupLifePlan}
                            planAvailable={hasGroupLife} />

                        <ReviewPlanExtendedDisplay
                            title={myCPGMidyearProps?.confirmationShortTermDisabilitySectionTitle}
                            plan={selectedShortTermDisability}
                            planAvailable={hasShortTermDisability} />

                        <ReviewPlanExtendedDisplay
                            title={myCPGMidyearProps?.confirmationLongTermDisabilitySectionTitle}
                            plan={selectedLongTermDisability}
                            planAvailable={hasLongTermDisability} />

                        <ReviewPlanExtendedDisplay
                            title={myCPGMidyearProps?.confirmationUeiSectionTitle}
                            plan={selectedUei}
                            planAvailable={hasUei} />

                    </DividerSection>
                    <ContentSpacer>
                        <ButtonWrapper flexEnd>
                            <button className={buttonClasses.primaryButton} onClick={() => RedirectToPage(myCpgHome)} type='button'>{myCPGMidyearProps?.myCpgHomeText}</button>

                        </ButtonWrapper>
                    </ContentSpacer>
                </MainContent >
                <Hidden smDown>
                    <SideBarContent>
                        <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem.contents} />
                    </SideBarContent>
                </Hidden>
            </PageContainer >
        </>
    );

}

